<template>
  <div :class="`suite-avatar ${size}`">
    <img
        v-if="src"
        :src="src"
        alt="User Avatar"
    />

    <div
        v-else
        class="avatar-initials"
        :style="fallbackStyle"
    >
      {{ initials }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  src?: string;
  name: string;
  size?: 'lg' | 'md' | 'sm';
}

const props = withDefaults(defineProps<Props>(), {
  name: 'User',
  size: 'md'
});

const initials = computed(() => {
  const trimmed = props.name?.trim();
  if (!trimmed) return '??';

  const parts = trimmed.split(/\s+/); // trims from inside
  return parts.map(part => part[0]).join('').toUpperCase();
});

// Generate a deterministic, clean background color based on the user's name
const fallbackStyle = computed(() => {
  const colors = [
    '#4ade80', '#60a5fa', '#f472b6', '#fb923c',
    '#a78bfa', '#2dd4bf', '#38bdf8', '#f59e0b'
  ];


  const colorIndex = props.name.length % colors.length;
  return {
    backgroundColor: colors[colorIndex],
  };
});
</script>

<style scoped>
.suite-avatar {
  display: inline-block;
  border-radius: 50%;
  overflow: hidden;
  vertical-align: middle;
  user-select: none;
  background-color: #e0e0e0;
}

.suite-avatar.sm {
  width: 32px;
  height: 32px;
}

.suite-avatar.md {
  width: 48px;
  height: 48px;
}

.suite-avatar.lg {
  width: 110px;
  height: 110px;
}

.suite-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.sm .avatar-initials {
  font-size: .75rem;
}

.md .avatar-initials {
  font-size: 1rem;
}

.lg .avatar-initials {
  font-size: 3rem;
}
</style>